// src/components/quizzes/BonusCompareFractions.js
//
// Compare-fractions bonus round: make the statement true.
// Runs on the shared bonus-round engine (kit/BonusShell.js) — the
// BonusDiamonds look, bombs, bonus stars, clocks, combos, a new question
// every few right answers, and 🏁 End game. Same contract as before:
// BonusRouter renders <ThisGame onBonusComplete questions placeholderImage />.

import React from "react";
import BonusShell, { Frac } from "./kit/BonusShell";

const SPAWN_MS = 450;
const MIN_TARGETS_ONSCREEN = 2;     // at least this many correct options on screen
const TARGET_SPEED_SLOWDOWN = 0.9;  // correct fall a bit slower
const DEN_MIN = 2;                   // smallest denominator to generate
const DEN_MAX = 10;                  // largest denominator to generate
const MIX_EQUAL_PROB = 0.18;         // chance an "=" problem appears

/** ======= FRACTION UTILS ======= */
const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));
const simplify = ({ n, d }) => {
  if (d === 0) return { n, d: 1 };
  const g = gcd(Math.abs(n), Math.abs(d)) || 1;
  const s = Math.sign(d) || 1;
  return { n: (n / g) * s, d: Math.abs(d / g) };
};

const fractionEquals = (a, b) => a.n * b.d === b.n * a.d;
const fractionLess = (a, b) => a.n * b.d < b.n * a.d;
const fractionGreater = (a, b) => a.n * b.d > b.n * a.d;

const formatFrac = ({ n, d }) => `${n}/${d}`;
const fracToSpeak = ({ n, d }) => {
  const ords = { 2: "halves", 3: "thirds", 4: "fourths", 5: "fifths", 6: "sixths", 7: "sevenths", 8: "eighths", 9: "ninths", 10: "tenths" };
  const denom = ords[d] || `${d}ths`;
  return `${n} ${denom}`;
};

const randomInt = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const randomFraction = () => {
  const d = randomInt(DEN_MIN, DEN_MAX);
  const n = randomInt(1, d - 1);
  return simplify({ n, d });
};

const makeAnchorFraction = () => randomFraction();

/** Problem like:
 *  { op:'>', anchor:{n,d}, blankSide:'right' }  => "a/b > ____"
 *  { op:'<', anchor:{n,d}, blankSide:'left' }   => "____ < a/b"
 *  { op:'=', anchor:{n,d}, blankSide:'right' }  => "a/b = ____"
 */
const makeRandomProblem = () => {
  const opPool = Math.random() < MIX_EQUAL_PROB ? ["="] : [">", "<", ">", "<"];
  const op = pick(opPool);
  const anchor = makeAnchorFraction();
  const blankSide = Math.random() < 0.5 ? "left" : "right";
  return { op, anchor, blankSide };
};

const speakableProblem = ({ op, anchor, blankSide }) => {
  const A = fracToSpeak(anchor);
  if (blankSide === "right") {
    if (op === ">") return `${A} is greater than blank`;
    if (op === "<") return `${A} is less than blank`;
    return `${A} equals blank`;
  } else {
    if (op === ">") return `blank is greater than ${A}`;
    if (op === "<") return `blank is less than ${A}`;
    return `blank equals ${A}`;
  }
};

const ruleText = ({ op, anchor, blankSide }) => {
  const A = formatFrac(anchor);
  if (op === "=") return `fractions equal to ${A}`;
  if (op === ">") return blankSide === "right" ? `fractions less than ${A}` : `fractions greater than ${A}`;
  return blankSide === "right" ? `fractions greater than ${A}` : `fractions less than ${A}`;
};

const isCorrectFor = ({ op, anchor, blankSide }, f) => {
  if (op === "=") return fractionEquals(f, anchor);
  if (op === ">") return blankSide === "right" ? fractionLess(f, anchor) : fractionGreater(f, anchor);
  return blankSide === "right" ? fractionGreater(f, anchor) : fractionLess(f, anchor);
};

// sample a CORRECT fraction
const pickCorrectValue = (problem) => {
  if (problem.op === "=") return { ...problem.anchor };
  for (let i = 0; i < 40; i++) {
    const f = randomFraction();
    if (isCorrectFor(problem, f)) return f;
  }
  return randomFraction();
};

// sample an INCORRECT fraction
const pickIncorrectValue = (problem) => {
  for (let i = 0; i < 60; i++) {
    const f = randomFraction();
    if (!isCorrectFor(problem, f)) return f;
  }
  // if we somehow failed, nudge the anchor
  const a = problem.anchor;
  const d = Math.min(Math.max(a.d, DEN_MIN), DEN_MAX);
  const n = Math.min(Math.max(a.n + (Math.random() < 0.5 ? -1 : 1), 1), d - 1);
  return simplify({ n, d });
};

// balance what spawns so both sides appear and keep at least MIN_TARGETS_ONSCREEN correct visible
const shouldSpawnCorrectBalanced = (problem, items) => {
  const correct = items.filter(it => isCorrectFor(problem, it.value)).length;
  const wrong = items.length - correct;
  if (correct < MIN_TARGETS_ONSCREEN) return true;
  if (correct < wrong) return true;
  if (wrong < correct) return false;
  return Math.random() < 0.5;
};

const Side = ({ p }) => <Frac n={p.anchor.n} d={p.anchor.d} />;
const bigOf = (p) =>
  p.blankSide === "right" ? (
    <>
      <Side p={p} /> {p.op} ▢
    </>
  ) : (
    <>
      ▢ {p.op} <Side p={p} />
    </>
  );
const sayRule = ({ op, anchor, blankSide }) => {
  const A = fracToSpeak(anchor);
  if (op === "=") return `fractions equal to ${A}`;
  if (op === ">") return blankSide === "right" ? `fractions less than ${A}` : `fractions greater than ${A}`;
  return blankSide === "right" ? `fractions greater than ${A}` : `fractions less than ${A}`;
};

const CompareFractionsBonus = (props) => (
  <BonusShell
    {...props}
    title="Fraction Face-Off!"
    emoji="🍕"
    sub="Catch the fractions that make the statement TRUE."
    intro="Compare the fractions. Catch the fractions that make the statement true. Each one is worth five points. Watch out for bombs! Press Start when you are ready."
    rules={[
      { icon: "✅", text: "Makes it true", pts: "+5" },
      { icon: "❌", text: "Makes it false", pts: "-1" },
    ]}
    init={() => makeRandomProblem()}
    prompt={(p) => ({ big: bigOf(p), small: `Catch ${ruleText(p)}!`, say: `${speakableProblem(p)}. Catch ${sayRule(p)}.` })}
    spawn={(p, onScreen) => (shouldSpawnCorrectBalanced(p, onScreen) ? pickCorrectValue(p) : pickIncorrectValue(p))}
    check={isCorrectFor}
    label={(f) => <Frac n={f.n} d={f.d} />}
    newEvery={4}
    spawnMs={SPAWN_MS}
    slowGood={TARGET_SPEED_SLOWDOWN}
  />
);

export default CompareFractionsBonus;
